import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getResolvedTags } from "../services/APIClient";
import {
  DEFAULT_TEACHER_DASHBOARD_NAVIGATION_VALUES,
  TEACHER_DASHBOARD_NAVIGATION_ELEMENT_TYPE,
  TEACHER_DASHBOARD_NAVIGATION_IDS,
  TEACHER_DASHBOARD_NAVIGATION_ITEMS,
  TEACHER_DASHBOARD_NAVIGATION_TAG_NAME,
} from "../config/teacherDashboardNavigation";
import TeacherNavigationUnavailable from "../components/TeacherNavigationUnavailable";

const TeacherNavigationContext = createContext({
  loading: false,
  isNavigationEnabled: () => true,
});

export function TeacherNavigationProvider({ children }) {
  const location = useLocation();
  const [values, setValues] = useState(DEFAULT_TEACHER_DASHBOARD_NAVIGATION_VALUES);
  const [loadedToken, setLoadedToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const currentToken = localStorage.getItem("token");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoadedToken(null);
      setValues(DEFAULT_TEACHER_DASHBOARD_NAVIGATION_VALUES);
      setLoading(false);
      return;
    }
    if (token === loadedToken) return;

    let cancelled = false;
    setLoading(true);
    getResolvedTags(
      TEACHER_DASHBOARD_NAVIGATION_ELEMENT_TYPE,
      TEACHER_DASHBOARD_NAVIGATION_TAG_NAME,
      TEACHER_DASHBOARD_NAVIGATION_ITEMS.map((item) => item.id)
    )
      .then((resolvedValues) => {
        if (!cancelled) {
          setValues({
            ...DEFAULT_TEACHER_DASHBOARD_NAVIGATION_VALUES,
            ...resolvedValues,
            [TEACHER_DASHBOARD_NAVIGATION_IDS.HOME]: "ON",
          });
        }
      })
      .catch((error) => {
        console.error("Failed to load Teacher Dashboard navigation:", error);
        if (!cancelled) {
          setValues(DEFAULT_TEACHER_DASHBOARD_NAVIGATION_VALUES);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadedToken(token);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [loadedToken, location.pathname]);

  const awaitingCurrentToken = Boolean(currentToken) && currentToken !== loadedToken;
  const contextValue = useMemo(
    () => ({
      loading: loading || awaitingCurrentToken,
      isNavigationEnabled: (navigationId) =>
        navigationId === TEACHER_DASHBOARD_NAVIGATION_IDS.HOME ||
        (!loading && !awaitingCurrentToken && values[navigationId] !== "OFF"),
    }),
    [awaitingCurrentToken, loading, values]
  );

  return (
    <TeacherNavigationContext.Provider value={contextValue}>
      {children}
    </TeacherNavigationContext.Provider>
  );
}

export function useTeacherNavigation() {
  return useContext(TeacherNavigationContext);
}

export function TeacherNavigationGate({ navigationId, navigationIds, children }) {
  const { loading, isNavigationEnabled } = useTeacherNavigation();
  const requiredNavigationIds = navigationIds || [navigationId];

  if (loading) {
    return (
      <div className="spinner-container">
        <div className="big-ball-spinner" />
      </div>
    );
  }

  if (!requiredNavigationIds.filter(Boolean).every(isNavigationEnabled)) {
    return <TeacherNavigationUnavailable />;
  }

  return children;
}
